import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/** Welche Konfigurationslisten sich in den Einstellungen pflegen lassen (A41, A51). */
export type ListenTyp = 'versandarten' | 'checklisten_punkte';

/** Ein Eintrag einer Konfigurationsliste, wie die Pflege ihn zeigt. */
export type ListenEintrag = {
  id: number;
  /** Der Schlüssel. Unveränderlich, sobald der Eintrag existiert. */
  code: string;
  label: string;
  sort_order: number;
  aktiv: boolean;
  /**
   * Wofür das Programm diesen Code auswertet — oder null.
   *
   * Kommt vom Server (`api/lib/listenpflege.php`), damit es nur eine
   * Wahrheit darüber gibt. Stünde die Liste hier, warnte sie irgendwann
   * vor einem Code, den niemand mehr auswertet — oder schwiege zu einem
   * neuen (Fallstrick 15).
   */
  hinweis: string | null;
} & {
  /**
   * Das Zusatzfeld dieser Liste, falls sie eines hat — heute `scope`
   * bei den Checklisten-Punkten (A51). Sein NAME steht in
   * `ListenZusatz.feld` und kommt vom Server, deshalb wird hier über
   * den Namen zugegriffen statt über ein festes Feld.
   *
   * Als Schnittmenge und nicht als Feld der Form oben: So bleiben `id`,
   * `code` und die anderen streng typisiert, und nur der dynamische
   * Zugriff ist `unknown` — er wird an der einen Stelle geprüft, die
   * ihn liest.
   *
   * Gesetzt wird der Wert beim ANLEGEN und nie wieder; `ListenPatch`
   * kennt ihn deshalb nicht.
   */
  [zusatzfeld: string]: unknown;
};

/**
 * Beschreibung des einen Zusatzfeldes einer Liste — oder null.
 *
 * Kommt vom Server (`api/lib/listenpflege.php`, LISTEN_ZUSATZFELD), aus
 * demselben Grund wie `hinweis`: Die erlaubten Werte sind eine ENUM der
 * Datenbank. Stünde die Liste hier, liefe sie irgendwann daneben
 * (Fallstrick 15).
 */
export type ListenZusatz = {
  feld: string;
  titel: string;
  hilfe: string;
  vorgabe: string;
  /** Wert → Beschriftung, in der Reihenfolge des Servers. */
  werte: Record<string, string>;
  /**
   * Wert → Warnung, was diese Wahl heute bedeutet — nur für die Werte,
   * die eine haben (A51a).
   *
   * Steht wie `werte` auf dem Server: Der Satz ist eine Aussage über
   * `api/bestellungen/detail.php`, nicht über die Oberfläche. Hier
   * gepflegt würde er stehen bleiben, wenn die fehlende Ansicht
   * gebaut ist.
   */
  warnung?: Record<string, string>;
};

/**
 * Eine Liste zum PFLEGEN — mit den stillgelegten Einträgen.
 *
 * Nicht `useVersandarten()`: Der Haken dort füttert die AUSWAHL und
 * bekommt vom Server nur `aktiv = 1`. Wer einen stillgelegten Eintrag
 * nicht sieht, kann ihn auch nicht zurückholen.
 */
export function useListenPflege(typ: ListenTyp) {
  return useQuery({
    queryKey: ['listen-pflege', typ],
    queryFn: () =>
      apiFetch<{ typ: string; eintraege: ListenEintrag[]; zusatz: ListenZusatz | null }>(
        `/listen/liste.php?typ=${encodeURIComponent(typ)}`
      ),
    staleTime: 60_000
  });
}

export type ListenPatch = {
  typ: ListenTyp;
  id: number;
  label?: string;
  sort_order?: number;
  aktiv?: boolean;
};

export type ListenNeu = {
  typ: ListenTyp;
  code: string;
  label: string;
  /**
   * Der Wert des Zusatzfeldes, unter seinem eigenen Namen — z. B.
   * `scope: 'sendung'`. Nur beim ANLEGEN; `ListenPatch` kennt ihn
   * bewusst nicht.
   */
  zusatz?: { feld: string; wert: string };
};

/**
 * Nach jeder Änderung sind ZWEI Listen alt: die Pflege selbst und die
 * Auswahl (`lookups.php`), aus der Erfassung und Sendungs-Dialog
 * schöpfen. Wer nur die erste erneuert, legt eine Versandart still und
 * sieht sie im Formular weiter stehen, bis die Haltbarkeit abläuft.
 */
function listenNachLaden(qc: ReturnType<typeof useQueryClient>, typ: ListenTyp): void {
  void qc.invalidateQueries({ queryKey: ['listen-pflege', typ] });
  void qc.invalidateQueries({ queryKey: ['lookups', typ] });
}

export function useListenEintragAendern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ListenPatch) =>
      apiFetch<{ geaendert: string[]; eintrag: ListenEintrag }>('/listen/aendern.php', {
        method: 'PATCH',
        body: JSON.stringify(patch)
      }),
    onSuccess: (_d, patch) => listenNachLaden(qc, patch.typ)
  });
}

export function useListenEintragAnlegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ zusatz, ...neu }: ListenNeu) =>
      apiFetch<{ eintrag: ListenEintrag }>('/listen/anlegen.php', {
        method: 'POST',
        // Das Zusatzfeld geht unter SEINEM Namen hinaus (`scope: …`),
        // nicht als verschachteltes `zusatz`-Objekt: Der Endpunkt liest
        // die Feldnamen, die er aus der Whitelist kennt.
        body: JSON.stringify(zusatz ? { ...neu, [zusatz.feld]: zusatz.wert } : neu)
      }),
    onSuccess: (_d, neu) => listenNachLaden(qc, neu.typ)
  });
}
